import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import { View, Text, Platform } from 'react-native';
import { TooltipWrapperProps } from './TooltipWrapper.types';
import styles from './TooltipWrapper.styles';

// Sur web, utiliser un portal pour rendre le tooltip au-dessus de tout (hors stacking context)
let createPortal: any = null;
if (Platform.OS === 'web') {
  try { createPortal = require('react-dom').createPortal; } catch {}
}

export const TooltipWrapper: FunctionComponent<TooltipWrapperProps> = ({ 
  children, 
  tooltip,
  style,
  tooltipStyle,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<any>(null);
  const containerRef = useRef<any>(null);
  const tooltipRef = useRef<any>(null);
  const [initialRect, setInitialRect] = useState<any>(null);
  // compute and set final tooltip position when initialRect and tooltipRef are available
  useTooltipPosition(initialRect, tooltipRef, setTooltipPos);
  
  return (
    <View 
      ref={containerRef}
      style={style}
      onPointerEnter={() => {
        // Compute bounding rect, then wait for the tooltip DOM node to render
        try {
          if (typeof window !== 'undefined' && containerRef.current?.getBoundingClientRect) {
            const rect = containerRef.current.getBoundingClientRect();
            setInitialRect(rect);
          } else {
            setInitialRect(null);
          }
        } catch (e) {
          setInitialRect(null);
        }
        setShowTooltip(true);
      }}
      onPointerLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (() => {
        const tooltipElement = (
          <View
            ref={tooltipRef}
            style={[
              styles.tooltip,
              tooltipStyle,
              // If tooltipPos computed, use fixed absolute coordinates; otherwise
              // fallback to relative absolute positioning above the element.
              tooltipPos ? { position: 'fixed' as any, left: tooltipPos.left, top: tooltipPos.top } : {},
            ]}
          >
            <Text style={styles.tooltipText}>{tooltip}</Text>
          </View>
        );
        // Sur web, rendre dans un portal pour sortir du stacking context du parent
        if (createPortal && typeof document !== 'undefined') {
          return createPortal(tooltipElement, document.body);
        }
        return tooltipElement;
      })()}
    </View>
  );
};

// Measure tooltip size after it is rendered and compute final fixed coords
function useTooltipPosition(initialRect: any, tooltipRef: any, setTooltipPos: any) {
  useEffect(() => {
    if (!initialRect) return;
    const compute = () => {
      try {
        if (typeof window === 'undefined') return;
        const node = tooltipRef.current;
        if (!node) return;
        // offsetWidth is available in web DOM
        const tw = node.offsetWidth || 0;
        const th = node.offsetHeight || 0;
        // center above the element
        let left = initialRect.left + (initialRect.width / 2) - (tw / 2);
        const minLeft = 8;
        const maxLeft = Math.max(8, window.innerWidth - tw - 8);
        left = Math.max(minLeft, Math.min(left, maxLeft));
        const top = initialRect.top - th - 8;
        setTooltipPos({ position: 'fixed', left, top });
      } catch (e) {
        // noop
      }
    };

    // Defer to next tick to ensure tooltipRef is mounted
    const id = window.setTimeout(compute, 0);
    // Also recompute on resize
    window.addEventListener('resize', compute);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', compute);
    };
  }, [initialRect, tooltipRef, setTooltipPos]);
}